/**
 * Applying a write to the local library before Simkl confirms it.
 *
 * Without this, tapping a season did nothing visible for 1.6 seconds: the
 * screen reads from the library, and the library only changed once a full
 * refetch came back. The write itself takes Simkl 183ms — the wait was almost
 * entirely the app's own doing, and the silence is what made it feel broken
 * rather than slow.
 *
 * These are the same edits the server will make, applied immediately. The
 * refetch that follows is the correction, not the source: if Simkl disagrees,
 * its answer wins on arrival.
 *
 * Only episode-level actions are modelled. A season mark is expanded into its
 * episodes before it gets here, so there is one code path rather than two that
 * can drift.
 */
import type { LibraryShow, WatchedSeason } from "@/api/schemas";
import type { EpisodeTarget } from "@/api/writes";
import { detailWithheld } from "@/lib/seasons";
import type { OutboxAction } from "@/store/outbox";

function key(season: number, episode: number): string {
  return `${String(season)}:${String(episode)}`;
}

/** Rebuilds `seasons` with `targets` added or removed, and the count to match. */
function withEpisodes(
  show: LibraryShow,
  targets: readonly EpisodeTarget[],
  watched: boolean,
): LibraryShow {
  const affected = new Set(targets.map((target) => key(target.season, target.episode)));
  const seasons = new Map<number, Map<number, WatchedSeason["episodes"][number]>>();
  for (const season of show.seasons) {
    const episodes = new Map<number, WatchedSeason["episodes"][number]>();
    for (const episode of season.episodes) {
      if (watched || !affected.has(key(season.number, episode.number))) {
        episodes.set(episode.number, episode);
      }
    }
    seasons.set(season.number, episodes);
  }

  if (watched) {
    const at = new Date().toISOString();
    for (const target of targets) {
      const episodes = seasons.get(target.season) ?? new Map();
      // Keep an existing watchedAt: re-marking something already watched is a
      // no-op at Simkl, and inventing a new timestamp would be a lie.
      if (!episodes.has(target.episode))
        episodes.set(target.episode, { number: target.episode, watchedAt: at });
      seasons.set(target.season, episodes);
    }
  }

  const rebuilt: WatchedSeason[] = [...seasons]
    .map(([number, episodes]) => ({
      number,
      episodes: [...episodes.values()].toSorted((a, b) => a.number - b.number),
    }))
    .filter((season) => season.episodes.length > 0)
    .toSorted((a, b) => a.number - b.number);

  const watchedEpisodes = rebuilt.reduce((sum, season) => sum + season.episodes.length, 0);
  return { ...show, seasons: rebuilt, watchedEpisodes };
}

/**
 * Whether the server data already reflects an edit.
 *
 * An edit is held over fetched data until this is true. Simkl does not
 * necessarily read back its own write immediately, and a refetch that lands
 * first would otherwise wipe the change off the screen — which is what
 * "it marks it and then unmarks it" was.
 */
export function isSatisfied(shows: readonly LibraryShow[], edit: OptimisticEdit): boolean {
  const show = shows.find((candidate) => candidate.show.ids.simkl === edit.simklId);
  // The show is gone from the library entirely; nothing left to reconcile.
  if (show === undefined) return true;
  // A finished show comes back without its per-episode detail, so the counts
  // are the only evidence available. Everything aired is watched.
  if (detailWithheld(show)) return edit.watched;
  const watched = new Set<string>();
  for (const season of show.seasons) {
    for (const episode of season.episodes) watched.add(key(season.number, episode.number));
  }
  return edit.targets.every(
    (target) => watched.has(key(target.season, target.episode)) === edit.watched,
  );
}

/** The episodes an edit covers, order-independent, for comparing two edits. */
export function editKey(edit: OptimisticEdit): string {
  return edit.targets
    .map((target) => `${String(target.season)}:${String(target.episode)}`)
    .toSorted()
    .join(",");
}

/**
 * Two edits that undo each other exactly.
 *
 * The outbox refuses to send a write and its exact inverse — they have no net
 * effect and Simkl does not reliably apply them in order.
 */
export function isInverse(a: OptimisticEdit, b: OptimisticEdit): boolean {
  return a.simklId === b.simklId && a.watched !== b.watched && editKey(a) === editKey(b);
}

/**
 * Removes from the held edits anything a newer edit has since overruled.
 *
 * An exact inverse is the easy case. The general one is partial: mark a
 * ten-episode season, then say "I only got as far as episode three", and the
 * later edit contradicts seven episodes of the earlier one. Simkl applies
 * both correctly — it ends at three — but the earlier edit was still being
 * held, unsatisfiable by definition, and re-applied over every fetch for
 * ninety seconds. The season read as fully watched the whole time.
 *
 * So a newer edit takes the episodes it covers away from the older ones, and
 * an edit left with nothing is dropped.
 */
export function supersede<T extends OptimisticEdit>(held: readonly T[], edit: OptimisticEdit): T[] {
  const claimed = new Set(
    edit.targets.map((target) => `${String(target.season)}:${String(target.episode)}`),
  );
  const kept: T[] = [];
  for (const candidate of held) {
    if (candidate.simklId !== edit.simklId || candidate.watched === edit.watched) {
      kept.push(candidate);
      continue;
    }
    const targets = candidate.targets.filter(
      (target) => !claimed.has(`${String(target.season)}:${String(target.episode)}`),
    );
    if (targets.length > 0) kept.push({ ...candidate, targets });
  }
  return kept;
}

export interface OptimisticEdit {
  simklId: number;
  targets: readonly EpisodeTarget[];
  watched: boolean;
}

/** Returns the library with the edit applied, or the same array if it is a no-op. */
export function applyEdit(
  shows: readonly LibraryShow[],
  edit: OptimisticEdit,
): readonly LibraryShow[] {
  if (edit.targets.length === 0) return shows;
  let changed = false;
  const next = shows.map((show) => {
    if (show.show.ids.simkl !== edit.simklId) return show;
    changed = true;
    return withEpisodes(show, edit.targets, edit.watched);
  });
  return changed ? next : shows;
}

/**
 * How an action changes the library before Simkl answers.
 *
 * Only the episode writes are modelled. Adding or removing a show changes
 * which shows exist, which the refetch settles; guessing at it locally would
 * be a second source of truth for no gain.
 */
export function editFor(action: OutboxAction): OptimisticEdit | null {
  switch (action.kind) {
    case "watch":
      return { simklId: action.target.simklId, targets: [action.target], watched: true };
    case "unwatch":
      return { simklId: action.target.simklId, targets: [action.target], watched: false };
    case "watch-many":
    case "unwatch-many": {
      const first = action.targets[0];
      if (first === undefined) return null;
      return {
        simklId: first.simklId,
        targets: action.targets,
        watched: action.kind === "watch-many",
      };
    }
    default:
      return null;
  }
}
